import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Upload, Trash2, Edit, Users, LogOut, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { BooksManager } from '../components/admin/BooksManager';
import { GenZManager } from '../components/admin/GenZManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('resources');
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'resources') {
        const { data } = await axios.get(`${API}/resources?limit=1000`, { withCredentials: true });
        setResources(data);
      } else if (activeTab === 'categories') {
        const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
        setCategories(data);
      } else if (activeTab === 'subscribers') {
        const { data } = await axios.get(`${API}/newsletter/subscribers`, { withCredentials: true });
        setSubscribers(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteResource = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      await axios.delete(`${API}/resources/${id}`, { withCredentials: true });
      toast.success('Resource deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <p className="font-body text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <BookOpen className="h-6 w-6 text-brand-accent" />
            <span className="font-heading font-semibold text-xl text-brand-primary">
              Admin Dashboard
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="font-body text-sm text-slate-600">{user.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-body font-medium text-slate-700 hover:text-brand-accent transition-colors duration-300"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Tabs */}
        <div className="mb-8 border-b border-slate-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('resources')}
              className={`pb-4 px-2 font-body font-medium transition-colors duration-300 border-b-2 ${
                activeTab === 'resources'
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-slate-600 hover:text-brand-primary'
              }`}
              data-testid="tab-resources"
            >
              Resources
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`pb-4 px-2 font-body font-medium transition-colors duration-300 border-b-2 ${
                activeTab === 'categories'
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-slate-600 hover:text-brand-primary'
              }`}
              data-testid="tab-categories"
            >
              Categories
            </button>
            <button
              onClick={() => setActiveTab('subscribers')}
              className={`pb-4 px-2 font-body font-medium transition-colors duration-300 border-b-2 ${
                activeTab === 'subscribers'
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-slate-600 hover:text-brand-primary'
              }`}
              data-testid="tab-subscribers"
            >
              Subscribers
            </button>
            <button
              onClick={() => setActiveTab('books')}
              className={`pb-4 px-2 font-body font-medium transition-colors duration-300 border-b-2 ${
                activeTab === 'books'
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-slate-600 hover:text-brand-primary'
              }`}
              data-testid="tab-books"
            >
              Books
            </button>
            <button
              onClick={() => setActiveTab('genz')}
              className={`pb-4 px-2 font-body font-medium transition-colors duration-300 border-b-2 ${
                activeTab === 'genz'
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-slate-600 hover:text-brand-primary'
              }`}
              data-testid="tab-genz"
            >
              Gen Z Lessons
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'resources' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl font-medium text-brand-primary">
                Manage Resources
              </h2>
              <button
                onClick={() => {
                  setEditingResource(null);
                  setShowResourceModal(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-all duration-300"
                data-testid="add-resource-button"
              >
                <Plus className="h-4 w-4" />
                <span>Add Resource</span>
              </button>
            </div>

            {loading ? (
              <p className="font-body text-slate-600">Loading...</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
                <table className="w-full" data-testid="resources-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {resources.map((resource) => (
                      <tr key={resource.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-body text-sm text-slate-900">{resource.title}</td>
                        <td className="px-6 py-4 font-body text-sm text-slate-600">{resource.category}</td>
                        <td className="px-6 py-4 font-body text-sm text-slate-600">
                          {[
                            resource.mp3_url && 'Audio',
                            resource.pdf_url && 'PDF',
                            resource.youtube_url && 'Video'
                          ].filter(Boolean).join(', ') || 'Text'}
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-slate-600">
                          {new Date(resource.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button
                            onClick={() => handleDeleteResource(resource.id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700"
                            data-testid={`delete-resource-${resource.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div>
            <h2 className="font-heading text-2xl font-medium text-brand-primary mb-6">
              Manage Categories
            </h2>
            {loading ? (
              <p className="font-body text-slate-600">Loading...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category) => (
                  <div key={category.id} className="bg-white border border-slate-200 rounded shadow-sm p-6">
                    <h3 className="font-heading text-lg font-medium text-brand-primary mb-2">
                      {category.name}
                    </h3>
                    <p className="font-body text-sm text-slate-600">{category.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'books' && <BooksManager />}

        {activeTab === 'genz' && <GenZManager />}

        {activeTab === 'subscribers' && (
          <div>
            <h2 className="font-heading text-2xl font-medium text-brand-primary mb-6">
              Newsletter Subscribers
            </h2>
            {loading ? (
              <p className="font-body text-slate-600">Loading...</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden">
                <table className="w-full" data-testid="subscribers-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Subscribed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {subscribers.map((subscriber) => (
                      <tr key={subscriber.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-body text-sm text-slate-900">{subscriber.email}</td>
                        <td className="px-6 py-4 font-body text-sm text-slate-600">
                          {new Date(subscriber.subscribed_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Resource Modal */}
      {showResourceModal && (
        <ResourceModal
          resource={editingResource}
          categories={categories}
          onClose={() => {
            setShowResourceModal(false);
            setEditingResource(null);
          }}
          onSuccess={() => {
            setShowResourceModal(false);
            setEditingResource(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

const ResourceModal = ({ resource, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: resource?.title || '',
    description: resource?.description || '',
    category: resource?.category || '',
    tags: resource?.tags?.join(', ') || '',
    author: resource?.author || 'RestoreMyWord',
    youtube_url: resource?.youtube_url || ''
  });
  const [files, setFiles] = useState({
    thumbnail: null,
    pdf: null,
    mp3: null
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData();
      form.append('title', formData.title);
      form.append('description', formData.description);
      form.append('category', formData.category);
      form.append('tags', formData.tags);
      form.append('author', formData.author);
      if (formData.youtube_url) form.append('youtube_url', formData.youtube_url);
      if (files.thumbnail) form.append('thumbnail', files.thumbnail);
      if (files.pdf) form.append('pdf', files.pdf);
      if (files.mp3) form.append('mp3', files.mp3);

      if (resource) {
        await axios.put(`${API}/resources/${resource.id}`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Resource updated successfully');
      } else {
        await axios.post(`${API}/resources`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Resource created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error('Failed to save resource');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-heading text-xl font-medium text-brand-primary">
            {resource ? 'Edit Resource' : 'Add Resource'}
          </h3>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
              data-testid="resource-title-input"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm resize-none"
              data-testid="resource-description-input"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
              data-testid="resource-category-select"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Tags (comma-separated)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
              placeholder="faith, prayer, hope"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">YouTube URL (optional)</label>
            <input
              type="url"
              name="youtube_url"
              value={formData.youtube_url}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Thumbnail</label>
            <input
              type="file"
              name="thumbnail"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-slate-200 rounded font-body text-sm"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">PDF File</label>
            <input
              type="file"
              name="pdf"
              accept=".pdf"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-slate-200 rounded font-body text-sm"
            />
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">MP3 Audio</label>
            <input
              type="file"
              name="mp3"
              accept="audio/*"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-slate-200 rounded font-body text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 font-body font-medium text-slate-700 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-all duration-300 disabled:opacity-50"
              data-testid="save-resource-button"
            >
              {loading ? 'Saving...' : 'Save Resource'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
